import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useAuth, useToast, useBasic, useModal } from 'contexts';
import { getRegisterTypes, updateUserProfile, getCertificado } from 'services';
import InputMask from 'react-input-mask';
import { ModalPrintCertificate } from 'components/ModalPrintCertificate';

import './style.scss';

type Props = {
  content: any[];
  className?: string;
  [key: string]: any;
};

type InputsForm = {
  [key: string]: any;
};

export function CompleteRegister({ content, className, ...rest }: Props) {
  const { user, refreshUser } = useAuth();
  const [sending, setSending] = useState(false);
  const { toastify } = useToast();
  const { showModal, closeModal } = useModal();
  const [content2, setContent] = useState(content);

  const [certificado, setCertificado] = useState<any>();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<InputsForm>();

  useEffect(() => {
    printCertified();

    const updatedContent = content.map(field => {
      if (field.name === 'cpf') {
        return {
          ...field,
          mask: '999.999.999-99',
        };
      }
      return field;
    });

    setContent(updatedContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, content]);

  async function printCertified() {
    let total = 0;
    const register = await getRegisterTypes();
    const certificado = await getCertificado();

    setCertificado(certificado);

    const filteredRegister = register.attributes.filter((e: any) => !e.required);

    filteredRegister.forEach((e: any) => {
      const campoName = e.name;
      if (!user) return;
      if (!(campoName in user.extra)) {
        total++;
      }
    });

    if (total === 0) {
      closeModal();
      showModal('text', <ModalPrintCertificate certifield={certificado} />, 'text-black');
    }
  }

  const onSubmit: SubmitHandler<InputsForm> = async data => {
    setSending(true);

    const updatedUser = { ...user };

    Object.keys(data).forEach((fieldName, fieldValue) => {
      if (!(fieldName in updatedUser.extra)) {
        updatedUser.extra[fieldName] = data[fieldName];
      }
    });

    updatedUser.name = data.name;

    updateUserProfile(updatedUser)
      .then(res => {
        if (res) {
          toastify({
            content: 'Cadastro atualizado com sucesso.',
            type: 'success',
          });
        }
      })
      .finally(() => {
        setSending(false);
        refreshUser();

        showModal('text', <ModalPrintCertificate certifield={certificado} />, 'text-black');
      });
  };

  if (!content) return null;

  return (
    <section id="completeRegister" className={className}>
      <h2 className="text-lg mb-3">Complete seu cadastro</h2>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <ul>
          {content2.map((field, index) => (
            <li key={index}>
              {field.type === 'select' && (
                <>
                  <label htmlFor={field.name}>{field.title}:</label>
                  <select
                    id={field.name}
                    aria-invalid={errors[field.name] ? 'true' : 'false'}
                    {...register(field.name, { required: !field.required && 'Campo obrigatório.' })}
                    disabled={sending}
                  >
                    <option value="">Selecione</option>
                    {field.static_dropdown.map((item: any, i: any) => (
                      <option value={item.value} key={i}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  {errors[field.name] && <span role="alert">{String(errors[field.name]?.message)}</span>}
                </>
              )}

              {field.type === 'text' && (
                <>
                  <label htmlFor={field.name}>{field.title}:</label>
                  {field.mask ? (
                    <InputMask
                      mask={field.mask}
                      maskPlaceholder={null}
                      {...register(field.name, { required: !field.required && 'Campo obrigatório.' })}
                      disabled={sending}
                    >
                      {(inputProps: any) => (
                        <input
                          {...inputProps}
                          type="text"
                          id={field.name}
                          aria-invalid={errors[field.name] ? 'true' : 'false'}
                          placeholder={field.title}
                        />
                      )}
                    </InputMask>
                  ) : (
                    <input
                      type="text"
                      id={field.name}
                      aria-invalid={errors[field.name] ? 'true' : 'false'}
                      {...register(field.name, { required: !field.required && 'Campo obrigatório.' })}
                      disabled={sending}
                      placeholder={field.title}
                    />
                  )}
                  {errors[field.name] && <span role="alert">{String(errors[field.name]?.message)}</span>}
                </>
              )}

              {field.type === 'checkbox' && !field.static_dropdown && (
                <>
                  <label htmlFor={field.name}>
                    <input
                      type={field.type}
                      id={field.name}
                      aria-invalid={errors[field.name] ? 'true' : 'false'}
                      {...register(field.name, { required: field.required && 'Campo obrigatório.' })}
                      disabled={sending}
                    />
                    {field.title}:
                  </label>
                  {errors[field.name] && <span role="alert">{String(errors[field.name]?.message)}</span>}
                </>
              )}

              {field.type === 'checkbox' && field.static_dropdown && (
                <>
                  <p className="mb-2 text-[1.2em]">{field.title}:</p>
                  <ul>
                    {field.static_dropdown.map((option: any, idx: any) => (
                      <li key={idx}>
                        <label htmlFor={option.value}>
                          <input
                            className="mr-1"
                            type="checkbox"
                            id={option.value}
                            aria-invalid={errors[`${field.name}.${option.value}`] ? 'true' : 'false'}
                            {...register(`${field.name}.${option.value}`)}
                            disabled={sending}
                          />
                          {option.label}
                        </label>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </li>
          ))}
        </ul>

        <button type="submit" disabled={sending}>
          Enviar
        </button>
      </form>
    </section>
  );
}
